import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, apiClient } from '@/lib/api/client';
import { useAuthStore } from '@/lib/auth/store';
import { ensureArray } from '@/lib/utils/ensure-array';
import type {
  BlacklistDndRemoveOp,
  BlacklistRow,
  DndAddTarget,
  SearchHitRow,
  WhitelistRow,
} from './types';

export const numberActionKeys = {
  all: ['number-actions'] as const,
  blacklist: (userId: string | number | null) =>
    ['number-actions', 'blacklist', userId] as const,
  whitelist: (userId: string | number | null) =>
    ['number-actions', 'whitelist', userId] as const,
};

function useInvalidateAll() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: numberActionKeys.all });
}

/* ============================================================
 *  Blacklist
 * ============================================================ */

/** GET /api/obd/blacklist/{userId} */
export function useBlacklist() {
  const userId = useAuthStore((s) => s.userId);
  return useQuery({
    queryKey: numberActionKeys.blacklist(userId),
    queryFn: () => api.get<BlacklistRow[]>(`/api/obd/blacklist/${userId}`),
    enabled: Boolean(userId),
    staleTime: 30_000,
    select: (d) => ensureArray<BlacklistRow>(d),
  });
}

/** POST /api/obd/blacklist — add a single mobile number to the blacklist. */
export function useAddBlacklist() {
  const userId = useAuthStore((s) => s.userId);
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: (mobile: string) =>
      api.post<{ message?: string }>('/api/obd/blacklist', {
        userId: Number(userId),
        mobile,
      }),
    onSuccess: () => invalidate(),
  });
}

/** DELETE /api/obd/blacklist/{blacklistId} — remove an entry by id. */
export function useDeleteBlacklist() {
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: (blacklistId: number) =>
      api.delete<{ message?: string }>(`/api/obd/blacklist/${blacklistId}`),
    onSuccess: () => invalidate(),
  });
}

/** POST /api/obd/blistupload (multipart) — bulk blacklist add via .txt/.csv. */
export function useUploadBlacklistFile() {
  const userId = useAuthStore((s) => s.userId);
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: (file: File) => {
      const fd = new FormData();
      fd.append('blistFile', file);
      fd.append('userId', String(userId));
      return apiClient
        .post<{ message?: string }>('/api/obd/blistupload', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
        .then((r) => r.data);
    },
    onSuccess: () => invalidate(),
  });
}

/* ============================================================
 *  DND  (SADMIN tooling)
 * ============================================================ */

/** POST /api/obd/addDND — add a single number to DND. */
export function useAddDnd() {
  const userId = useAuthStore((s) => s.userId);
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: (mobile: string) =>
      api.post<{ message?: string }>('/api/obd/addDND', {
        userId: Number(userId),
        mobile,
        operation: 'add',
      }),
    onSuccess: () => invalidate(),
  });
}

/** POST /api/obd/dndUpload (multipart) — bulk DND add via .txt/.csv. */
export function useUploadDndFile() {
  const userId = useAuthStore((s) => s.userId);
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: (file: File) => {
      const fd = new FormData();
      fd.append('blistFile', file);
      fd.append('userId', String(userId));
      fd.append('operation', 'add');
      return apiClient
        .post<{ message?: string }>('/api/obd/dndUpload', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
        .then((r) => r.data);
    },
    onSuccess: () => invalidate(),
  });
}

/**
 * Convenience wrapper — pick the right single-number endpoint based on the
 * dropdown choice. Keeps the page code declarative.
 */
export function useAddBlacklistOrDnd() {
  const blist = useAddBlacklist();
  const dnd = useAddDnd();
  const isPending = blist.isPending || dnd.isPending;
  const mutate = (
    target: DndAddTarget,
    mobile: string,
    handlers: {
      onSuccess?: (res: { message?: string }) => void;
      onError?: (err: unknown) => void;
    },
  ) => {
    const fn = target === 'BLACKLIST' ? blist : dnd;
    fn.mutate(mobile, handlers);
  };
  return { mutate, isPending };
}

/**
 * Convenience wrapper — pick the right bulk-upload endpoint based on the
 * dropdown choice.
 */
export function useUploadBlacklistOrDndFile() {
  const blist = useUploadBlacklistFile();
  const dnd = useUploadDndFile();
  const isPending = blist.isPending || dnd.isPending;
  const mutate = (
    target: DndAddTarget,
    file: File,
    handlers: {
      onSuccess?: (res: { message?: string }) => void;
      onError?: (err: unknown) => void;
    },
  ) => {
    const fn = target === 'BLACKLIST' ? blist : dnd;
    fn.mutate(file, handlers);
  };
  return { mutate, isPending };
}

/* ============================================================
 *  Whitelist
 * ============================================================ */

/** GET /api/obd/whitelist/{userId} */
export function useWhitelist() {
  const userId = useAuthStore((s) => s.userId);
  return useQuery({
    queryKey: numberActionKeys.whitelist(userId),
    queryFn: () => api.get<WhitelistRow[]>(`/api/obd/whitelist/${userId}`),
    enabled: Boolean(userId),
    staleTime: 30_000,
    select: (d) => ensureArray<WhitelistRow>(d),
  });
}

/** POST /api/obd/whitelist — single-number add. */
export function useAddWhitelist() {
  const userId = useAuthStore((s) => s.userId);
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: (mobile: string) =>
      api.post<{ message?: string }>('/api/obd/whitelist', {
        userId: Number(userId),
        mobile,
        operation: 'add',
      }),
    onSuccess: () => invalidate(),
  });
}

/** POST /api/obd/superadmin/delete/Whitelist — remove a single whitelist entry. */
export function useDeleteWhitelist() {
  const userId = useAuthStore((s) => s.userId);
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: (mobile: string) =>
      api.post<{ message?: string }>(
        '/api/obd/superadmin/delete/Whitelist',
        {
          userId: Number(userId),
          mobile,
          operation: 'remove',
        },
      ),
    onSuccess: () => invalidate(),
  });
}

/* ============================================================
 *  Cross-list search
 * ============================================================ */

/**
 * POST /api/obd/search/Blacklist/DND/Whitelist — query whether a mobile is
 * present in any of the three lists. Returns a row whose `status` field is
 * a hyphen-joined token list (e.g. 'BLACKLIST-DND').
 */
export function useSearchNumber() {
  const userId = useAuthStore((s) => s.userId);
  return useMutation({
    mutationFn: (mobile: string) =>
      api.post<SearchHitRow>('/api/obd/search/Blacklist/DND/Whitelist', {
        userId: Number(userId),
        mobile,
      }),
  });
}

/**
 * POST /api/obd/superadmin/delete/Blacklist/DND — remove from blacklist OR
 * DND (operation discriminates).
 */
export function useDeleteBlacklistOrDnd() {
  const userId = useAuthStore((s) => s.userId);
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: (args: { mobile: string; operation: BlacklistDndRemoveOp }) =>
      api.post<{ message?: string }>(
        '/api/obd/superadmin/delete/Blacklist/DND',
        {
          userId: Number(userId),
          mobile: args.mobile,
          operation: args.operation,
        },
      ),
    onSuccess: () => invalidate(),
  });
}
